import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cable, ChevronRight, Globe, Info, Network, ShieldAlert, Trash2, Wifi, X } from 'lucide-react';
import { apiClient } from '../../api';
import ConfirmModal from '../UI/ConfirmModal';
import { snmpLine, uptime } from './topology';
import { inputCls, inputStyle, fieldLabel, fieldLabelStyle, monoLabel, primaryBtn, primaryBtnStyle, errMessage } from '../UI/ink';
import {
  BAND_NAME, ago, bandBg, bandFg, connectionsOf, critLabel, hasAgent, isRiskyPort, riskParts, shortDate, statusOf, typeLabel,
} from './model';

const OPEN_STATUSES = 'new,triaged,investigating,contained';
const SEV_BAND = { critical: 'crit', high: 'high', medium: 'med', low: 'low', info: 'low' };
export const DEVICE_TYPES = [['server', 'Server'], ['network', 'Network device'], ['workstation', 'Endpoint'], ['iot', 'IoT device'], ['unknown', 'Unknown']];

const portList = (ports) => ports.map(p => `${p.port}/${p.protocol || 'tcp'}`).join(', ');
const card = { background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' };
const Heading = ({ children, count }) => (
  <span className={`flex items-center justify-between ${monoLabel}`} style={{ color: 'var(--ink-faint)' }}>
    <span>{children}</span>{count != null && <span>{count}</span>}
  </span>
);

const Glyph = ({ a, size = 14 }) => (
  <span
    aria-hidden="true"
    className="box-border shrink-0"
    style={{
      width: size, height: size,
      borderRadius: a.kind === 'server' ? 3 : a.kind === 'network' ? 1 : size / 2,
      border: `1.5px ${a.discovered ? 'dashed' : 'solid'} ${a.band === 'unk' ? 'var(--ink-faint)' : bandFg(a.band)}`,
      background: a.band === 'unk' ? 'transparent' : bandBg(a.band),
      transform: a.kind === 'network' ? 'rotate(45deg) scale(0.8)' : undefined,
    }}
  />
);

// Nothing selected: the riskiest assets, one click from their details.
const Overview = ({ assets, onSelect }) => {
  const top = assets.filter(a => a.risk != null).sort((x, y) => y.risk - x.risk).slice(0, 6);
  return (
    <div className="p-5 flex flex-col gap-3.5 flex-grow min-h-0">
      <Heading count={top.length || null}>HIGHEST RISK</Heading>
      {top.length ? (
        <div className="flex flex-col gap-2 overflow-y-auto min-h-0">
          {top.map(a => (
            <button key={a.id} type="button" onClick={() => onSelect(a.id)} className="min-h-[54px] shrink-0 box-border flex items-center gap-3 px-3 py-2 rounded-[10px] border text-left" style={card}>
              <Glyph a={a} />
              <span className="flex-grow min-w-0 flex flex-col gap-[3px]">
                <span className="font-mono text-[12.5px] truncate">{a.host}</span>
                <span className="text-[11.5px] truncate" style={{ color: 'var(--ink-faint)' }}>
                  {a.findings?.total || 0} findings · {a.vulns} CVEs{a.internetFacing ? ' · internet-facing' : ''}
                </span>
              </span>
              <span className="font-mono text-[13px] shrink-0">{a.risk}</span>
              <ChevronRight className="w-[15px] h-[15px] shrink-0" style={{ color: 'var(--ink-faint)' }} strokeWidth={1.8} />
            </button>
          ))}
        </div>
      ) : (
        <p className="m-0 text-[13px] leading-normal" style={{ color: 'var(--ink-muted)' }}>No asset has a risk score yet.</p>
      )}
      <p className="m-0 mt-auto pt-3.5 border-t text-xs leading-normal" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-faint)' }}>
        Select any asset to see why it scores what it does, what it talks to and what to do next.
      </p>
    </div>
  );
};

// The score split into its parts, so "82" comes with a reason.
const RiskWhy = ({ a }) => {
  const parts = riskParts(a);
  const total = Math.max(100, parts.reduce((n, p) => n + p.value, 0));
  return (
    <div className="flex flex-col gap-3">
      <span className="flex items-center gap-4">
        <span className="font-heading text-[40px] font-semibold leading-none">{a.risk ?? '—'}</span>
        <span className="inline-flex items-center gap-1.5 h-[22px] px-2 rounded-md font-mono text-[10.5px] font-medium tracking-[0.06em]" style={{ background: a.band === 'unk' ? 'var(--surf-raised)' : bandBg(a.band) }}>
          <span className="w-1.5 h-1.5 rounded-[1px]" style={{ background: a.band === 'unk' ? 'transparent' : bandFg(a.band) }} />
          {a.risk == null ? 'NOT SCORED YET' : `${BAND_NAME[a.band]} RISK`}
        </span>
      </span>
      {parts.length > 0 && (
        <>
          <Heading>WHY {Math.round(a.risk)}</Heading>
          <span aria-hidden="true" className="flex h-2.5 gap-0.5 rounded-[5px] overflow-hidden" style={{ background: 'var(--surf-raised)' }}>
            {parts.map(p => <span key={p.key} style={{ width: `${(p.value / total) * 100}%`, background: p.color }} />)}
          </span>
          <span className="flex flex-col gap-2 text-[12.5px]">
            {parts.map(p => (
              <span key={p.key} className="flex justify-between gap-2 min-w-0">
                <span className="flex items-center gap-1.5 min-w-0" style={{ color: 'var(--ink-muted)' }}>
                  <span className="w-[7px] h-[7px] rounded-[1px] shrink-0" style={{ background: p.color }} /><span className="truncate">{p.label}</span>
                </span>
                <span className="font-mono shrink-0">+{Math.round(p.value)}</span>
              </span>
            ))}
          </span>
        </>
      )}
    </div>
  );
};

// Who this asset talks to, busiest first, with a bar for how often.
const Connections = ({ a, links, byId, onSelect }) => {
  const { peers, internet, unmanaged } = connectionsOf(links, a.id);
  const rows = [
    internet && { key: 'internet', internet: true, observations: internet.observations, last_seen: internet.last_seen, title: `Internet · ${internet.peers} address${internet.peers === 1 ? '' : 'es'}`, ips: internet.top_peers },
    ...peers.filter(p => byId[p.id]).map(p => ({ key: p.id, peer: byId[p.id], observations: p.observations, last_seen: p.last_seen })),
    unmanaged && { key: 'unmanaged', observations: unmanaged.observations, last_seen: unmanaged.last_seen, title: `Not on the map · ${unmanaged.peers} address${unmanaged.peers === 1 ? '' : 'es'}`, ips: unmanaged.top_peers },
  ].filter(Boolean);
  const max = Math.max(1, ...rows.map(r => r.observations));
  const body = (r) => (
    <>
      <span className="flex justify-between gap-2 text-[13px]">
        <span className="flex items-center gap-1.5 min-w-0">
          {r.internet && <Globe className="w-3 h-3 shrink-0" strokeWidth={2} style={{ color: 'var(--accent)' }} />}
          <span className={`truncate ${r.peer ? 'font-mono' : ''}`}>{r.peer ? r.peer.host : r.title}</span>
        </span>
        <span className="font-mono text-[11.5px] shrink-0" style={{ color: 'var(--ink-faint)' }}>{ago(r.last_seen)}</span>
      </span>
      <span aria-hidden="true" className="h-1 rounded-sm flex" style={{ background: 'var(--surf-raised)' }}>
        <span className="rounded-sm" style={{ width: `${Math.max(4, (r.observations / max) * 100)}%`, background: 'var(--accent)' }} />
      </span>
      {r.ips?.length > 0 && <span className="font-mono text-[11px] break-all" style={{ color: 'var(--ink-muted)' }}>{r.ips.map(t => t.ip).join(', ')}</span>}
    </>
  );
  return (
    <div className="flex flex-col gap-2">
      <Heading count={rows.length}>TALKS TO</Heading>
      {rows.map(r => (r.peer ? (
        <button key={r.key} type="button" onClick={() => onSelect(r.key)} className="flex flex-col gap-1.5 px-3 py-2.5 rounded-[10px] border text-left" style={card}>
          {body(r)}
        </button>
      ) : (
        <div key={r.key} className={`flex flex-col gap-1.5 px-3 py-2.5 rounded-[10px] border ${r.internet ? '' : 'border-dashed'}`} style={r.internet ? card : { borderColor: 'var(--surf-line-strong)' }}>
          {body(r)}
        </div>
      )))}
      {rows.length === 0 && (
        <span className="text-[13px] py-1" style={{ color: 'var(--ink-muted)' }}>
          {hasAgent(a) ? 'No connections reported in this window.' : 'No agent on this asset, and no agent saw it in a connection.'}
        </span>
      )}
    </div>
  );
};

// How the device reaches the network, from its agent's last heartbeat.
const NetworkLink = ({ network }) => {
  const { link, ap, gateway } = network || {};
  if (!link) return null;
  const wifi = link.type === 'wifi';
  const rows = [
    ...(wifi ? [
      ['Access point', ap ? (ap.suspicious ? 'Unknown access point' : ap.name || ap.key) : link.bssid || '—'],
      ['Network', link.ssid || '—'],
      ['Signal', link.signal_dbm != null ? `${link.signal_dbm} dBm${link.signal_dbm < -75 ? ' · weak' : ''}` : '—'],
      ['Band', [link.band, link.channel && `ch ${link.channel}`].filter(Boolean).join(' · ') || '—'],
    ] : [['Connection', link.type === 'wired' ? 'Cable' : 'Not known']]),
    ['Gateway', link.gateway_ip ? `${link.gateway_ip}${link.gateway_mac ? ` · ${link.gateway_mac}` : ''}` : '—'],
  ];
  return (
    <div className="flex flex-col gap-2">
      <Heading>NETWORK LINK</Heading>
      {(link.gateway_mismatch || ap?.suspicious) && (
        <div role="alert" className="flex gap-2 px-3 py-2.5 rounded-[10px] border text-[12.5px] leading-snug" style={{ borderColor: 'var(--sev-crit-fg)', background: 'var(--sev-crit-bg)' }}>
          <ShieldAlert className="w-4 h-4 shrink-0 mt-px" style={{ color: 'var(--sev-crit-fg)' }} strokeWidth={2} />
          <span>{ap?.suspicious ? 'Joined an access point that isn’t one of yours, using your network name.' : `Uses gateway ${link.gateway_ip}; your other devices use ${gateway?.ip}.`}</span>
        </div>
      )}
      <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 p-3.5 rounded-[10px] border" style={card}>
        {rows.map(([label, value], i) => (
          <div key={label} className={`flex flex-col gap-[3px] min-w-0 ${label === 'Gateway' ? 'col-span-2' : ''}`}>
            <span className="flex items-center gap-1.5 text-[11.5px]" style={{ color: 'var(--ink-faint)' }}>
              {i === 0 && (wifi ? <Wifi className="w-3 h-3" strokeWidth={2} /> : <Cable className="w-3 h-3" strokeWidth={2} />)}{label}
            </span>
            <span className="text-[13px] truncate font-mono" title={value}>{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// What SNMP says: the device's own health when it is polled, and for any device a
// bridge table placed, which access point or port it sits behind.
const SnmpDetails = ({ network }) => {
  const { snmp, placement, via } = network || {};
  if (!snmp && !placement) return null;
  const health = snmpLine(snmp);
  const rows = [
    ...(placement ? [
      ['Behind', via?.sys_name || placement.device_ip],
      [placement.wireless ? 'Radio' : 'Port', placement.port],
    ] : []),
    ...(snmp ? [
      ['Name', snmp.sys_name || '—'],
      ['Location', snmp.sys_location || '—'],
      ['Uptime', snmp.uptime_s != null ? uptime(snmp.uptime_s) : '—'],
      ['Ports', snmp.ports_total ? `${snmp.ports_up} of ${snmp.ports_total} up` : '—'],
      ['Port errors', `${snmp.port_errors} since restart`],
      ['Last polled', `${ago(snmp.polled_at)} ago`],
      ['Model', snmp.sys_descr || '—'],
    ] : []),
  ];
  return (
    <div className="flex flex-col gap-2">
      <Heading>SNMP</Heading>
      {snmp?.stale && (
        <span className="text-[12.5px]" style={{ color: 'var(--sev-med-fg)' }}>{health.text}. Devices behind it are no longer placed from its old table.</span>
      )}
      <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 p-3.5 rounded-[10px] border" style={card}>
        {rows.map(([label, value], i) => (
          <div key={label} className={`flex flex-col gap-[3px] min-w-0 ${label === 'Model' ? 'col-span-2' : ''}`}>
            <span className="flex items-center gap-1.5 text-[11.5px]" style={{ color: 'var(--ink-faint)' }}>
              {i === 0 && (placement ? (placement.wireless ? <Wifi className="w-3 h-3" strokeWidth={2} /> : <Cable className="w-3 h-3" strokeWidth={2} />) : <Network className="w-3 h-3" strokeWidth={2} />)}{label}
            </span>
            <span className={`text-[13px] ${label === 'Model' ? 'leading-snug' : 'truncate font-mono'}`} title={value}>{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const RegisteredDetails = ({ a, discoveredKids, links, network, byId, onSelect, onDelete }) => {
  const [issues, setIssues] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    let live = true;
    setIssues(null);
    apiClient.get(`/api/findings?server_id=${encodeURIComponent(a.id)}&status=${OPEN_STATUSES}&limit=3`)
      .then(r => { if (live) setIssues(r.data.findings || []); })
      .catch(() => { if (live) setIssues([]); });
    return () => { live = false; };
  }, [a.id]);

  const openCount = a.findings ? a.findings.total : issues?.length;
  const facts = [
    ['Criticality', `${a.criticality} / 10 · ${critLabel(a.criticality)}`],
    ['Operating system', a.os || '—'],
    ['Last seen', a.last_seen ? `${ago(a.last_seen)} ago` : 'Never'],
    ['Found on the network', discoveredKids.length ? discoveredKids.map(c => c.host).join(', ') : 'None'],
  ];

  return (
    <>
      <div className="flex-grow min-h-0 overflow-y-auto px-5 py-[18px] flex flex-col gap-[22px]">
        <RiskWhy a={a} />

        <div className="flex flex-col gap-2">
          <Heading count={openCount ?? '…'}>OPEN FINDINGS</Heading>
          {issues?.map(f => {
            const b = SEV_BAND[(f.severity || '').toLowerCase()] || 'low';
            return (
              <Link key={f.id} to={`/findings?finding=${encodeURIComponent(f.id)}`} className="flex items-stretch gap-2.5 px-3 py-2.5 rounded-[10px] border no-underline" style={{ ...card, color: 'var(--ink-text)' }}>
                <span aria-hidden="true" className="w-1 rounded-sm shrink-0" style={{ background: bandFg(b) }} />
                <span className="flex-grow min-w-0 flex flex-col gap-[3px]">
                  <span className="text-[13px] leading-snug">{f.title}</span>
                  <span className="text-[11.5px]" style={{ color: 'var(--ink-faint)' }}>{(f.severity || 'low').replace(/^./, c => c.toUpperCase())} · {ago(f.detected_at)} ago</span>
                </span>
              </Link>
            );
          })}
          {issues && issues.length === 0 && <span className="text-[13px] py-1" style={{ color: 'var(--ink-muted)' }}>No open findings.</span>}
          {(openCount > 0 || a.vulns > 0) && (
            <Link to="/findings" className="text-[12.5px] py-1" style={{ color: 'var(--ink-link)' }}>
              View all {openCount || 0} finding{openCount === 1 ? '' : 's'}{a.vulns ? ` and ${a.vulns} CVE${a.vulns === 1 ? '' : 's'}` : ''}
            </Link>
          )}
        </div>

        <NetworkLink network={network} />

        <SnmpDetails network={network} />

        <Connections a={a} links={links} byId={byId} onSelect={onSelect} />

        {a.ports.length > 0 && (
          <div className="flex flex-col gap-2">
            <Heading>OPEN PORTS</Heading>
            <span className="flex flex-wrap gap-1.5">
              {a.ports.map(p => {
                const risky = isRiskyPort(p.port, a.internetFacing);
                return (
                  <span key={`${p.port}/${p.protocol}`} title={risky ? 'Worth a look: remote access, file sharing or a database' : undefined} className="font-mono text-[11.5px] px-2 py-[3px] rounded-md border" style={risky ? { background: bandBg('high'), borderColor: bandFg('high') } : card}>
                    {p.port}/{p.protocol || 'tcp'}{p.service ? ` · ${p.service}` : ''}
                  </span>
                );
              })}
            </span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-x-4 gap-y-3 p-3.5 rounded-[10px] border" style={card}>
          {facts.map(([label, value]) => (
            <div key={label} className="flex flex-col gap-[3px] min-w-0">
              <span className="text-[11.5px]" style={{ color: 'var(--ink-faint)' }}>{label}</span>
              <span className="text-[13px] truncate" title={value}>{value}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2 px-5 py-4 border-t" style={{ borderColor: 'var(--surf-line)' }}>
        <Link to={`/assets/${a.id}`} className={`${primaryBtn} flex-grow whitespace-nowrap no-underline`} style={primaryBtnStyle}>Open asset</Link>
        <Link to="/response-actions" className="h-11 flex items-center px-3.5 rounded-[10px] border text-sm font-medium whitespace-nowrap no-underline" style={{ borderColor: 'var(--surf-line-strong)', color: 'var(--ink-text)' }}>Respond</Link>
        <Link to="/cases" className="h-11 flex items-center px-3.5 rounded-[10px] border text-sm font-medium whitespace-nowrap no-underline" style={{ borderColor: 'var(--surf-line-strong)', color: 'var(--ink-text)' }}>Cases</Link>
      </div>
      <button type="button" onClick={() => setConfirmDelete(true)} aria-label="Delete asset" title="Delete asset" className="absolute right-[64px] top-5 w-9 h-9 flex items-center justify-center rounded-[9px] border" style={{ borderColor: 'var(--surf-line)', color: 'var(--sev-crit-fg)' }}>
        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.8} />
      </button>
      <ConfirmModal
        isOpen={confirmDelete}
        title="Delete asset"
        message={`Delete ${a.host}? Its history and findings links are removed. This can’t be undone.`}
        confirmLabel="Delete"
        danger
        onConfirm={() => { setConfirmDelete(false); onDelete(a); }}
        onCancel={() => setConfirmDelete(false)}
      />
    </>
  );
};

// Discovered device selected: accept it inline instead of in a modal.
const AcceptForm = ({ a, parent, onDone, extra }) => {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setForm({
      hostname: a.unnamed ? '' : a.hostname,
      device_type: DEVICE_TYPES.some(([v]) => v === a.deviceType) ? a.deviceType : 'unknown',
      location: a.location || '',
      criticality_score: a.criticality || 5,
      is_internet_facing: a.internetFacing,
    });
    setError(null);
  }, [a]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (lifecycle) => {
    setBusy(true);
    setError(null);
    try {
      const body = lifecycle === 'IGNORED'
        ? { lifecycle_state: 'IGNORED' }
        : {
            lifecycle_state: 'REGISTERED',
            hostname: form.hostname.trim() || a.hostname || a.ip,
            device_type: form.device_type,
            location: form.location.trim() || null,
            criticality_score: form.criticality_score,
            is_internet_facing: form.is_internet_facing ? 1 : 0,
          };
      await apiClient.put(`/api/servers/${a.id}`, body);
      onDone(lifecycle, a);
    } catch (err) {
      setError(errMessage(err, lifecycle === 'IGNORED' ? 'Failed to dismiss.' : 'Failed to accept asset.'));
    } finally {
      setBusy(false);
    }
  };

  const crit = form.criticality_score || 5;
  const note = `Seen by ${parent?.host || 'an agent'} since ${shortDate(a.created_at)}${a.mac ? ` · MAC ${a.mac}` : ''}${a.ports.length ? ` · open ports ${portList(a.ports)}` : ''}. Not registered yet, so it has no agent and no full risk score.`;

  return (
    <div className="flex-grow min-h-0 overflow-y-auto px-5 py-[18px] flex flex-col gap-3.5">
      {extra}
      <div className="flex gap-2.5 px-3.5 py-3 rounded-[10px] border border-dashed text-[12.5px] leading-normal" style={{ borderColor: 'var(--surf-line-strong)', color: 'var(--ink-muted)' }}>
        <Info className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.8} />
        <span>{note}</span>
      </div>
      {error && <div role="alert" className="text-[13px]" style={{ color: 'var(--sev-crit-fg)' }}>{error}</div>}
      <form onSubmit={e => { e.preventDefault(); submit('REGISTERED'); }} className="m-0 flex flex-col gap-3.5">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="acc-name" className={fieldLabel} style={fieldLabelStyle}>Name</label>
          <input id="acc-name" type="text" value={form.hostname || ''} onChange={e => set('hostname', e.target.value)} placeholder={a.ip || 'e.g. nas-backup-01'} className={inputCls} style={inputStyle} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="acc-type" className={fieldLabel} style={fieldLabelStyle}>Device type</label>
            <select id="acc-type" value={form.device_type || 'unknown'} onChange={e => set('device_type', e.target.value)} className={inputCls} style={inputStyle}>
              {DEVICE_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="acc-loc" className={fieldLabel} style={fieldLabelStyle}>Location</label>
            <input id="acc-loc" type="text" value={form.location || ''} onChange={e => set('location', e.target.value)} placeholder="e.g. Block-A" className={inputCls} style={inputStyle} />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <span className={`flex justify-between ${fieldLabel}`} style={fieldLabelStyle}>
            <label htmlFor="acc-crit">Criticality</label>
            <span className="font-mono" style={{ color: 'var(--ink-text)' }}>{crit} / 10 · {critLabel(crit)}</span>
          </span>
          <input id="acc-crit" type="range" min="1" max="10" step="1" value={crit} onChange={e => set('criticality_score', Number(e.target.value))} className="w-full m-0" style={{ accentColor: 'var(--accent)' }} />
          <span className="flex justify-between font-mono text-[10.5px]" style={{ color: 'var(--ink-faint)' }}><span>1 LOW</span><span>4 MED</span><span>7 HIGH</span><span>9 CRIT</span></span>
        </div>
        <label className="flex items-center gap-2.5 min-h-8 text-[13.5px] cursor-pointer">
          <input type="checkbox" checked={!!form.is_internet_facing} onChange={e => set('is_internet_facing', e.target.checked)} className="m-0 w-4 h-4" style={{ accentColor: 'var(--accent)' }} />
          Internet-facing
        </label>
        <div className="flex gap-2 pt-1">
          <button type="submit" disabled={busy} className={`${primaryBtn} flex-grow`} style={primaryBtnStyle}>{busy ? 'Saving…' : 'Accept & register'}</button>
          <button type="button" disabled={busy} onClick={() => submit('IGNORED')} className="h-11 px-3.5 rounded-[10px] border text-sm font-medium disabled:opacity-50" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>Dismiss</button>
        </div>
      </form>
    </div>
  );
};

const Inspector = ({ asset, assets, byId, links, network, onSelect, onChanged, onDelete }) => {
  const st = asset ? statusOf(asset) : null;
  const origin = asset && (asset.discovered ? 'DISCOVERED' : hasAgent(asset) ? 'AGENT' : 'NO AGENT');
  return (
    <aside aria-label="Asset details" className="relative h-full min-h-[560px] rounded-[14px] border flex flex-col overflow-hidden" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
      {!asset ? (
        <Overview assets={assets} onSelect={onSelect} />
      ) : (
        <>
          <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-4 border-b" style={{ borderColor: 'var(--surf-line)' }}>
            <div className={`flex flex-col gap-1.5 min-w-0 ${asset.discovered ? '' : 'pr-11'}`}>
              <span className="font-mono text-[10.5px] tracking-[0.12em]" style={{ color: 'var(--ink-link)' }}>
                {[typeLabel(asset.deviceType), origin, asset.location].filter(Boolean).join(' · ').toUpperCase()}
              </span>
              <h2 className="m-0 font-heading text-[22px] font-semibold tracking-[-0.01em] truncate">{asset.unnamed ? 'Unknown device' : asset.host}</h2>
              <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[12.5px]" style={{ color: 'var(--ink-muted)' }}>
                <span className="font-mono">{asset.ip || '—'}</span>
                <span className="flex items-center gap-1.5"><span className="w-[7px] h-[7px] rounded-full" style={{ background: st.dot }} />{st.label}</span>
                {asset.internetFacing && (
                  <span className="flex items-center gap-[5px] px-[7px] py-px rounded-[5px] text-[11.5px]" style={{ background: 'var(--surf-raised)' }}>
                    <Globe className="w-[11px] h-[11px]" strokeWidth={2.2} style={{ color: 'var(--accent)' }} />Internet-facing
                  </span>
                )}
              </span>
            </div>
            <button type="button" onClick={() => onSelect(null)} aria-label="Close details" className="w-9 h-9 shrink-0 flex items-center justify-center rounded-[9px] border" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>
              <X className="w-3.5 h-3.5" strokeWidth={2} />
            </button>
          </div>
          {asset.discovered
            ? <AcceptForm a={asset} parent={byId[asset.discoveredBy]} onDone={onChanged} extra={<SnmpDetails network={network} />} />
            : <RegisteredDetails a={asset} discoveredKids={assets.filter(c => c.discoveredBy === asset.id)} links={links} network={network} byId={byId} onSelect={onSelect} onDelete={onDelete} />}
        </>
      )}
    </aside>
  );
};

export default Inspector;
